import { useCallback, useEffect, useMemo } from 'react';
import { useFlashTransition } from 'hooks/useFlashState';
import { BusinessVariable } from 'components/WYSIWYG/use-merge-fields';

export type BusinessVariableErrors = Partial<{
  business_variables: Partial<Record<keyof BusinessVariable, string[]>>[];
}>;

const variableErrorsOrder: Partial<Record<keyof BusinessVariable, number>> = {
  name: 0,
  value_string: 1,
};

type useErrorsProps = {
  errors: BusinessVariableErrors;
  setErrors: React.Dispatch<React.SetStateAction<BusinessVariableErrors>>;
  index: number;
};

export const useErrors = ({ errors, setErrors, index }: useErrorsProps) => {
  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const { business_variables } = errors;

  const variableError = (business_variables || [])[index];

  const hasError = Boolean(
    variableError && Object.values(variableError).length
  );

  const isFirstVariableError =
    hasError &&
    !(business_variables || []).some(
      (el, i) =>
        i < index && el && typeof el === 'object' && Object.values(el).length
    );

  const errorKey = useMemo<keyof BusinessVariable | null>(() => {
    return isFirstVariableError
      ? (Object.keys(variableError) as (keyof BusinessVariable)[]).sort(
          (a, b) => variableErrorsOrder[a]! - variableErrorsOrder[b]!
        )[0]
      : null;
  }, [isFirstVariableError, variableError]);

  useEffect(() => {
    if (errorKey && variableError[errorKey]) {
      flashErrorMessage(variableError[errorKey]);
    }
  }, [variableError, flashErrorMessage, errorKey]);

  const clearError = useCallback(
    (prop: keyof BusinessVariable) => {
      if (hasError) {
        if (showMessage && errorKey === prop) {
          cancelFlash();
        }

        setErrors((prev) => ({
          ...prev,
          business_variables: (prev.business_variables || []).map((el, i) => {
            if (i === index) {
              const { [prop]: _, ...rest } = el;
              return rest;
            }
            return el;
          }),
        }));
      }
    },
    [showMessage, cancelFlash, hasError, setErrors, errorKey, index]
  );

  const validate = useMemo(
    () => ({ showMessage, message }),
    [showMessage, message]
  );

  return {
    hasError,
    variableError,
    errorKey,
    validate,
    clearError,
  };
};
